'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'

const careStats = [
  { stat: '< 2s', label: 'Response time', detail: 'Voice, chat, or in-app' },
  { stat: '24 / 7', label: 'Always on', detail: 'No queues, no hold music' },
  {
    stat: '50+',
    label: 'Languages',
    detail: 'Almost any language',
  },
  {
    stat: '0',
    label: 'Missed requests',
    detail: 'Every interaction logged and acted on',
  },
]

const opsCallouts = [
  {
    stat: '12,860',
    label: 'Predicted demand units',
    detail: 'Forecast for the next 4-hour window',
  },
  {
    stat: 'Live',
    label: 'Fleet density heatmap',
    detail: 'Every vehicle positioned in real time',
  },
  {
    stat: '3 active',
    label: 'Optimization suggestions',
    detail: 'System proposes. Operator confirms.',
  },
  {
    stat: 'Auto',
    label: 'Pending actions',
    detail: 'Escalation and resolution pipeline',
  },
]

export function PlatformIntelligence() {
  return (
    <section className="py-24 bg-[#0d1117]">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16">
          <p className="text-xs font-mono tracking-widest text-[#00c8ff] uppercase mb-4">
            The Full Platform
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#e2e8f0] tracking-tight">
            Automated at the front.
            <br />
            Intelligent at the back.
          </h2>
          <p className="mt-4 text-[#94a3b8] max-w-2xl mx-auto text-lg">
            Every customer interaction is handled by AI — and every one of those
            interactions feeds the operations intelligence loop.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left: Customer Care Automation */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-[#1c2333] bg-[#07090f] overflow-hidden h-full flex flex-col"
          >
            <div className="px-8 pt-8 pb-6">
              <p className="text-xs font-mono text-[#00c8ff] tracking-widest uppercase mb-6">
                Customer Care Automation
              </p>
              <h3 className="text-2xl font-bold text-[#e2e8f0] mb-4">
                Every inquiry handled.
                <br />
                Every ticket solved.
              </h3>
              <p className="text-base text-[#94a3b8] leading-relaxed">
                TidyCalls was built for care: voice calls, chat, in-app requests
                — any language, any channel. But a platform that only answers is
                not a platform. We connected the front door to the operations
                engine. Now the same AI that handles a call can open a gate,
                book a slot, relese a stuck charging cable, dispatch a
                technician, or adjust pricing — in the same platform.
              </p>
            </div>

            {/* Operators image */}
            <div className="relative mx-4 rounded-xl overflow-hidden border border-[#1c2333]">
              <Image
                src="/moshizen-operators.jpg"
                alt="TidyCalls care team — AI-assisted operators handling voice and chat"
                width={1024}
                height={576}
                className="w-full object-cover object-top"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 divide-x divide-y divide-[#1c2333] border-t border-[#1c2333] mt-auto">
              {careStats.map((item) => (
                <div key={item.label} className="px-5 py-4">
                  <p className="text-xl font-bold text-[#e2e8f0] font-mono mb-0.5">
                    {item.stat}
                  </p>
                  <p className="text-xs font-medium text-[#00c8ff] mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-xs text-[#94a3b8] leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Operations Intelligence */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="rounded-2xl border border-[#1c2333] bg-[#07090f] overflow-hidden h-full flex flex-col"
          >
            <div className="px-8 pt-8 pb-6">
              <p className="text-xs font-mono text-[#00c8ff] tracking-widest uppercase mb-6">
                Operations Intelligence
              </p>
              <h3 className="text-2xl font-bold text-[#e2e8f0] mb-4">
                Predictive ops.
                <br />
                Financial visibility.
              </h3>
              <p className="text-base text-[#94a3b8] leading-relaxed">
                Real-time demand forecasting, origin-destination analysis, fleet
                capacity planning, yield analysis, CO2 budgets, and proactive
                maintenance — all visible in one ops room. Test simulted
                policies and see their effect instantly. Every metric is live.
                Every action is one confirmation away, with natural language,
                naturally.
              </p>
            </div>

            {/* Dashboard image */}
            <div
              className="relative mx-4 rounded-xl overflow-hidden border border-[#1c2333]"
              style={{ aspectRatio: '1408/768' }}
            >
              <Image
                src="/images/content/predictive-ops-dashboard.png"
                alt="Operations intelligence dashboard — predictive demand, fleet heatmap, optimization"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-[#07090f]/80 backdrop-blur-sm border border-[#1c2333] rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00c8ff] animate-pulse" />
                <span className="text-xs font-mono text-[#00c8ff] tracking-widest">
                  LIVE
                </span>
              </div>
            </div>

            {/* Callout stats */}
            <div className="grid grid-cols-2 divide-x divide-y divide-[#1c2333] border-t border-[#1c2333] mt-auto">
              {opsCallouts.map((item) => (
                <div key={item.label} className="px-5 py-4">
                  <p className="text-xl font-bold text-[#e2e8f0] font-mono mb-0.5">
                    {item.stat}
                  </p>
                  <p className="text-xs font-medium text-[#00c8ff] mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-xs text-[#94a3b8] leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
